import React, { useState, useRef, useEffect } from 'react';
import FileService from '../services/FileService';
import { useFlowContext } from '../context/FlowContext';
import './RasaIntentManager.css';

const RasaIntentManager = ({ onSave, onClose }) => {
  const { userId, userGroup, training, setTraining, showNotification } = useFlowContext();

  // View state
  const [viewMode, setViewMode] = useState('self'); // 'default', 'organisation', 'self'
  const [organisations, setOrganisations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');

  // State for intents
  const [intents, setIntents] = useState([]);
  const [selectedIntent, setSelectedIntent] = useState(null);
  const [newIntentName, setNewIntentName] = useState('');
  const [editingIntent, setEditingIntent] = useState(null);

  // State for intent examples
  const [intentExamples, setIntentExamples] = useState([]);
  const [newExample, setNewExample] = useState('');
  const [editingExample, setEditingExample] = useState(null);

  // State for slots
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [newSlotName, setNewSlotName] = useState('');
  const [editingSlot, setEditingSlot] = useState(null);

  // State for slot questions
  const [slotQuestions, setSlotQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [editingQuestion, setEditingQuestion] = useState(null);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [loadingExamples, setLoadingExamples] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  // Modals state
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [sourceOrgIdForCopy, setSourceOrgIdForCopy] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [trainingStatus, setTrainingStatus] = useState('idle'); // 'idle', 'training', 'finished', 'failed'
  const [isSaved, setIsSaved] = useState(false); // Force save before training
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [currentStage, setCurrentStage] = useState(''); // PARSING, INIT, CONFIG, TRAINING, COMPLETE
  const [capturedIntents, setCapturedIntents] = useState([]);
  const [capturedEntities, setCapturedEntities] = useState([]);
  const logEndRef = useRef(null);

  // Autocomplete for slot names in example input
  const [showSlotDropdown, setShowSlotDropdown] = useState(false);
  const [slotQuery, setSlotQuery] = useState('');
  const [filteredSlots, setFilteredSlots] = useState([]);
  const [slotDropdownIndex, setSlotDropdownIndex] = useState(0);
  const exampleInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load initial data and organisations
  useEffect(() => {
    if (userId) {
      loadData();
      setIsSaved(false); // Force save on mode/org change
    }
  }, [userId, viewMode, selectedOrgId]);

  const loadData = async () => {
    try {
      setLoading(true);

      let targetUserId = userId;
      let status = 0;

      if (viewMode === 'default') {
        targetUserId = 'admin'; // Global default
        status = 0;
      } else if (viewMode === 'organisation') {
        if (!selectedOrgId) {
          setIntents([]);
          setSlots([]);
          setLoading(false);
          return;
        }
        targetUserId = selectedOrgId;
        status = 1;
      } else {
        targetUserId = userId;
        status = 0;
      }

      const [intentsData, slotsData] = await Promise.all([
        FileService.getIntents(targetUserId, status),
        FileService.getSlots(targetUserId, status)
      ]);

      setIntents(intentsData);
      setSlots(slotsData);

      // Load organisations if superadmin and not already loaded
      if (userGroup.toLowerCase() === 'superadmin' && organisations.length === 0) {
        const orgs = await FileService.getOrganisations();
        setOrganisations(orgs);
      }

    } catch (error) {
      console.error('Error loading data:', error);
      showNotification('Error loading data: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load examples when intent is selected
  useEffect(() => {
    if (selectedIntent) {
      loadIntentExamples();
    }
  }, [selectedIntent]);

  // Load questions when slot is selected
  useEffect(() => {
    if (selectedSlot) {
      loadSlotQuestions();
    }
  }, [selectedSlot]);

  // Update filtered slots when slotQuery changes
  useEffect(() => {
    if (slotQuery) {
      const q = slotQuery.toLowerCase();
      setFilteredSlots(slots.filter(s => s.slot.toLowerCase().includes(q)));
    } else {
      setFilteredSlots(slots);
    }
  }, [slotQuery, slots]);

  const loadIntentExamples = async () => {
    if (!selectedIntent) return;
    try {
      setLoadingExamples(true);
      const examples = await FileService.getIntentExamples(selectedIntent.id);
      setIntentExamples(examples);
    } catch (error) {
      console.error('Error loading examples:', error);
      showNotification('Error loading examples: ' + error.message, 'error');
    } finally {
      setLoadingExamples(false);
    }
  };

  const loadSlotQuestions = async () => {
    if (!selectedSlot) return;
    try {
      setLoadingQuestions(true);
      const questions = await FileService.getSlotQuestions(selectedSlot.id);
      setSlotQuestions(questions);
    } catch (error) {
      console.error('Error loading questions:', error);
      showNotification('Error loading questions: ' + error.message, 'error');
    } finally {
      setLoadingQuestions(false);
    }
  };

  // Intent CRUD operations
  const handleAddIntent = async () => {
    if (!newIntentName.trim()) return;

    let targetUserId = userId;
    let status = 0;

    if (viewMode === 'default') {
      targetUserId = 'admin';
      status = 0;
    } else if (viewMode === 'organisation') {
      targetUserId = selectedOrgId;
      status = 1;
    }

    try {
      const result = await FileService.addIntent(targetUserId, newIntentName.toUpperCase(), status);
      if (result.id) {
        setIntents([...intents, { id: result.id, intent: newIntentName.toUpperCase(), user_id: targetUserId, status }]);
        setNewIntentName('');
        setIsSaved(false);
      }
    } catch (error) {
      showNotification(error.message, 'error');
    }
  };

  const handleUpdateIntent = async () => {
    if (!editingIntent || !editingIntent.intent.trim()) return;
    try {
      await FileService.updateIntent(editingIntent.id, editingIntent.intent.toUpperCase());
      setIntents(intents.map(i => i.id === editingIntent.id ? { ...i, intent: editingIntent.intent.toUpperCase() } : i));
      setEditingIntent(null);
      setIsSaved(false);
    } catch (error) {
      showNotification('Error updating intent: ' + error.message, 'error');
    }
  };

  const handleDeleteIntent = async (intent) => {
    if (!window.confirm(`Are you sure you want to delete intent "${intent.intent}" and all its examples?`)) return;
    try {
      await FileService.deleteIntent(intent.id);
      setIntents(intents.filter(i => i.id !== intent.id));
      setIsSaved(false);
      if (selectedIntent && selectedIntent.id === intent.id) {
        setSelectedIntent(null);
        setIntentExamples([]);
      }
    } catch (error) {
      showNotification('Error deleting intent: ' + error.message, 'error');
    }
  };

  // Intent Examples CRUD operations
  const handleAddExample = async () => {
    if (!selectedIntent || !newExample.trim()) return;
    try {
      const result = await FileService.addIntentExample(selectedIntent.user_id, selectedIntent.id, newExample.trim(), selectedIntent.status);
      if (result.id) {
        setIntentExamples([...intentExamples, { id: result.id, intent_examples: newExample.trim() }]);
        setNewExample('');
        setIsSaved(false);
      }
    } catch (error) {
      showNotification('Error adding example: ' + error.message, 'error');
    }
  };

  const handleUpdateExample = async () => {
    if (!editingExample || !editingExample.intent_examples.trim()) return;
    try {
      await FileService.updateIntentExample(editingExample.id, editingExample.intent_examples.trim());
      setIntentExamples(intentExamples.map(e => e.id === editingExample.id ? { ...e, intent_examples: editingExample.intent_examples.trim() } : e));
      setEditingExample(null);
      setIsSaved(false);
    } catch (error) {
      showNotification('Error updating example: ' + error.message, 'error');
    }
  };

  const handleDeleteExample = async (example) => {
    if (!window.confirm(`Are you sure you want to delete this example?`)) return;
    try {
      await FileService.deleteIntentExample(example.id);
      setIntentExamples(intentExamples.filter(e => e.id !== example.id));
      setIsSaved(false);
    } catch (error) {
      showNotification('Error deleting example: ' + error.message, 'error');
    }
  };

  // Slot CRUD operations
  const handleAddSlot = async () => {
    if (!newSlotName.trim()) return;

    let targetUserId = userId;
    let status = 0;

    if (viewMode === 'default') {
      targetUserId = 'admin';
      status = 0;
    } else if (viewMode === 'organisation') {
      targetUserId = selectedOrgId;
      status = 1;
    }

    try {
      const result = await FileService.addSlot(targetUserId, newSlotName.toUpperCase(), 'text', status);
      if (result.id) {
        setSlots([...slots, { id: result.id, slot: newSlotName.toUpperCase(), user_id: targetUserId, status }]);
        setNewSlotName('');
        setIsSaved(false);
      }
    } catch (error) {
      showNotification('Error adding slot: ' + error.message, 'error');
    }
  };

  const handleUpdateSlot = async () => {
    if (!editingSlot || !editingSlot.slot.trim()) return;
    try {
      await FileService.updateSlot(editingSlot.id, editingSlot.slot.toUpperCase(), 'text');
      setSlots(slots.map(s => s.id === editingSlot.id ? { ...s, slot: editingSlot.slot.toUpperCase() } : s));
      setEditingSlot(null);
      setIsSaved(false);
    } catch (error) {
      showNotification('Error updating slot: ' + error.message, 'error');
    }
  };

  const handleDeleteSlot = async (slot) => {
    if (!window.confirm(`Are you sure you want to delete slot "${slot.slot}" and all its questions?`)) return;
    try {
      await FileService.deleteSlot(slot.id);
      setSlots(slots.filter(s => s.id !== slot.id));
      setIsSaved(false);
      if (selectedSlot && selectedSlot.id === slot.id) {
        setSelectedSlot(null);
        setSlotQuestions([]);
      }
    } catch (error) {
      showNotification('Error deleting slot: ' + error.message, 'error');
    }
  };

  // Slot Questions CRUD operations
  const handleAddQuestion = async () => {
    if (!selectedSlot || !newQuestion.trim()) return;
    try {
      const result = await FileService.addSlotQuestion(selectedSlot.user_id, selectedSlot.id, newQuestion.trim(), selectedSlot.status);
      if (result.id) {
        setSlotQuestions([...slotQuestions, { id: result.id, slot_questions: newQuestion.trim() }]);
        setNewQuestion('');
        setIsSaved(false);
      }
    } catch (error) {
      showNotification('Error adding question: ' + error.message, 'error');
    }
  };

  const handleUpdateQuestion = async () => {
    if (!editingQuestion || !editingQuestion.slot_questions.trim()) return;
    try {
      await FileService.updateSlotQuestion(editingQuestion.id, editingQuestion.slot_questions.trim());
      setSlotQuestions(slotQuestions.map(q => q.id === editingQuestion.id ? { ...q, slot_questions: editingQuestion.slot_questions.trim() } : q));
      setEditingQuestion(null);
      setIsSaved(false);
    } catch (error) {
      showNotification('Error updating question: ' + error.message, 'error');
    }
  };

  const handleDeleteQuestion = async (question) => {
    if (!window.confirm(`Are you sure you want to delete this question?`)) return;
    try {
      await FileService.deleteSlotQuestion(question.id);
      setSlotQuestions(slotQuestions.filter(q => q.id !== question.id));
      setIsSaved(false);
    } catch (error) {
      showNotification('Error deleting question: ' + error.message, 'error');
    }
  };

  // CSV Upload functionality
  const handleCSVUpload = async () => {
    if (!csvFile || !selectedIntent) {
      showNotification('Please select a CSV file and an intent first', 'error');
      return;
    }

    try {
      setCsvUploading(true);
      const examples = await FileService.parseCSVForExamples(csvFile);

      let addedCount = 0;
      for (const example of examples) {
        try {
          await FileService.addIntentExample(selectedIntent.user_id, selectedIntent.id, example, selectedIntent.status);
          addedCount++;
        } catch (error) {
          console.log(`Skipping duplicate or failed example: ${example}`);
        }
      }

      await loadIntentExamples();
      showNotification(`Successfully added ${addedCount} examples from CSV`, 'success');
      setCsvFile(null);
      setShowImportModal(false);
      setIsSaved(false);
    } catch (error) {
      showNotification('Error uploading CSV: ' + error.message, 'error');
    } finally {
      setCsvUploading(false);
    }
  };

  // Copy functionality
  const handleCopyOrganisation = async () => {
    if (!sourceOrgIdForCopy || !selectedOrgId) {
      showNotification('Please select source and target organisations', 'error');
      return;
    }

    try {
      setLoading(true);
      const result = await FileService.copyOrganisationData(sourceOrgIdForCopy, selectedOrgId, 1); // status 1 for org
      if (result.success) {
        showNotification(result.message, 'success');
        await loadData();
        setShowCopyModal(false);
        setIsSaved(false);
      }
    } catch (error) {
      showNotification('Error copying data: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const targetUserId = viewMode === 'organisation' ? selectedOrgId : (viewMode === 'default' ? 'admin' : userId);
      const status = viewMode === 'organisation' ? 1 : 0;

      if (!targetUserId) {
        showNotification('Please select an organisation to save.', 'error');
        return;
      }

      const result = await FileService.saveRasaData(targetUserId, status);
      showNotification('All the intents and slots are saved, train them to initiate the server', 'success');
      setIsSaved(true);
      setTrainingStatus('idle'); // Reset status if we save again
    } catch (error) {
      showNotification('Error saving: ' + error.message, 'error');
    }
  };

  const handleTrain = async () => {
    try {
      const targetOrgId = viewMode === 'organisation' ? selectedOrgId : (viewMode === 'default' ? 'admin' : userId);
      const status = viewMode === 'organisation' ? 1 : 0;

      if (!targetOrgId) {
        showNotification('Please select an organisation to train.', 'error');
        return;
      }

      setTraining(true);
      setTrainingStatus('training');
      setLogs(['Starting training...']);
      setShowLogs(true);
      setCurrentStage('PARSING');
      setCapturedIntents([]);
      setCapturedEntities([]);

      // Start polling for logs
      const logInterval = setInterval(async () => {
        try {
          const logData = await FileService.getRasaLogs(targetOrgId, status);
          if (logData && logData.logs) {
            setLogs(logData.logs);

            // Parse markers
            let isTerminal = false;
            logData.logs.forEach(line => {
              if (line.startsWith('STAGE:')) {
                const stage = line.replace('STAGE:', '').trim();
                setCurrentStage(stage);
                if (stage === 'FAILED') {
                  setTrainingStatus('failed');
                  isTerminal = true;
                } else if (stage === 'COMPLETE') {
                  setTrainingStatus('finished');
                  isTerminal = true;
                }
              } else if (line.startsWith('INTENTS_FOUND:')) {
                const intents = line.replace('INTENTS_FOUND:', '').trim().split(',').map(s => s.trim()).filter(Boolean);
                setCapturedIntents(intents);
              } else if (line.startsWith('ENTITIES_FOUND:')) {
                const entities = line.replace('ENTITIES_FOUND:', '').trim().split(',').map(s => s.trim()).filter(Boolean);
                setCapturedEntities(entities);
              }
            });

            if (isTerminal) {
              clearInterval(logInterval);
              setTraining(false);
              if (currentStage === 'FAILED') {
                showNotification('Training failed. Check logs for details.', 'error');
              } else {
                showNotification('RASA training completed successfully!', 'success');
              }
              // Reset status after 5 seconds
              setTimeout(() => setTrainingStatus('idle'), 5000);
            }
          }
        } catch (e) {
          console.error('Error polling logs:', e);
        }
      }, 2000);

      const result = await FileService.trainRasa(targetOrgId, status);
      showNotification(result.message, 'success');

    } catch (error) {
      showNotification('Error training: ' + error.message, 'error');
      setTrainingStatus('idle');
      setCurrentStage('');
      setTraining(false);
    }
  };

  // Scroll logs to bottom
  useEffect(() => {
    if (logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  // Handle input for @ autocomplete
  const handleExampleInput = (e) => {
    const value = e.target.value;
    setNewExample(value);
    const cursorPos = e.target.selectionStart;
    const textBefore = value.slice(0, cursorPos);
    const atIdx = textBefore.lastIndexOf('@');
    if (atIdx !== -1 && (atIdx === 0 || /[\s\(\[]/.test(textBefore[atIdx - 1]))) {
      const afterAt = textBefore.slice(atIdx + 1);
      if (/^[\w-]*$/.test(afterAt)) {
        setShowSlotDropdown(true);
        setSlotQuery(afterAt);
        setSlotDropdownIndex(0);
        return;
      }
    }
    setShowSlotDropdown(false);
    setSlotQuery('');
  };

  const insertSlotMention = (slotName) => {
    if (!exampleInputRef.current) return;
    const input = exampleInputRef.current;
    const value = newExample;
    const cursorPos = input.selectionStart;
    const textBefore = value.slice(0, cursorPos);
    const atIdx = textBefore.lastIndexOf('@');
    if (atIdx === -1) return;
    const beforeAt = value.slice(0, atIdx);
    const afterAt = value.slice(cursorPos);
    const mention = '[value](' + slotName + ')';
    const newVal = beforeAt + mention + afterAt;
    setNewExample(newVal);
    setShowSlotDropdown(false);
    setSlotQuery('');
    setTimeout(() => {
      const pos = beforeAt.length + mention.length;
      input.setSelectionRange(pos, pos);
      input.focus();
    }, 0);
  };

  const handleExampleKeyDown = (e) => {
    if (showSlotDropdown && filteredSlots.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSlotDropdownIndex(i => (i + 1) % filteredSlots.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSlotDropdownIndex(i => (i - 1 + filteredSlots.length) % filteredSlots.length);
      } else if (e.key === 'Enter') {
        if (slotDropdownIndex >= 0 && slotDropdownIndex < filteredSlots.length) {
          e.preventDefault();
          insertSlotMention(filteredSlots[slotDropdownIndex].slot);
        }
      } else if (e.key === 'Escape') {
        setShowSlotDropdown(false);
      }
    }
  };

  return (
    <div className="rasa-manager-overlay">
      <div className="rasa-manager-dialog">
        <div className="rasa-manager-header">
          <h2>Manage Intents and Slots</h2>
          <button onClick={onClose} className="delete-btn" style={{ fontSize: '2rem' }}>×</button>
        </div>

        <div className="rasa-manager-controls">
          <div className="view-mode-selector">
            <button
              className={`view-mode-btn ${viewMode === 'default' ? 'active' : ''}`}
              onClick={() => setViewMode('default')}
            >
              Default
            </button>
            {userGroup.toLowerCase() === 'superadmin' && (
              <button
                className={`view-mode-btn ${viewMode === 'organisation' ? 'active' : ''}`}
                onClick={() => setViewMode('organisation')}
              >
                Organisation
              </button>
            )}
            <button
              className={`view-mode-btn ${viewMode === 'self' ? 'active' : ''}`}
              onClick={() => setViewMode('self')}
            >
              My Intent
            </button>
          </div>

          {viewMode === 'organisation' && userGroup.toLowerCase() === 'superadmin' && (
            <div className="org-selector-container">
              <label>Organisation:</label>
              <select
                className="org-select"
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
              >
                <option value="">Select Organisation</option>
                {organisations.map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="rasa-manager-content">
          {/* Sidebar - Intent and Slot lists */}
          <div className="rasa-manager-sidebar">
            <div className="sidebar-section">
              <div className="section-header">
                <h3>Intents</h3>
              </div>
              <div className="add-input-group">
                <input
                  type="text"
                  value={newIntentName}
                  onChange={(e) => setNewIntentName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddIntent()}
                  placeholder="New intent name"
                />
                <button className="btn btn-primary btn-sm" onClick={handleAddIntent}>Add</button>
              </div>
              <div className="list-container">
                {loading ? <p style={{ padding: '15px', color: '#666' }}>Loading...</p> : intents.map((intent) => (
                  <div
                    key={intent.id}
                    className={`list-item ${selectedIntent?.id === intent.id ? 'active' : ''}`}
                    onClick={() => { setSelectedIntent(intent); setSelectedSlot(null); }}
                  >
                    <span>{intent.intent}</span>
                    <button className="delete-btn" onClick={(e) => { e.stopPropagation(); handleDeleteIntent(intent); }}>×</button>
                  </div>
                ))}
              </div>
            </div>

            <div className="sidebar-section">
              <div className="section-header">
                <h3>Slots</h3>
              </div>
              <div className="add-input-group">
                <input
                  type="text"
                  value={newSlotName}
                  onChange={(e) => setNewSlotName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddSlot()}
                  placeholder="New slot name"
                />
                <button className="btn btn-primary btn-sm" onClick={handleAddSlot}>Add</button>
              </div>
              <div className="list-container">
                {loading ? <p style={{ padding: '15px', color: '#666' }}>Loading...</p> : slots.map((slot) => (
                  <div
                    key={slot.id}
                    className={`list-item ${selectedSlot?.id === slot.id ? 'active' : ''}`}
                    onClick={() => { setSelectedSlot(slot); setSelectedIntent(null); }}
                  >
                    <span>{slot.slot}</span>
                    <button className="delete-btn" onClick={(e) => { e.stopPropagation(); handleDeleteSlot(slot); }}>×</button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content - Examples or Questions */}
          <div className="rasa-manager-main">
            <div className="main-actions-bar">
              {viewMode === 'organisation' && (
                <button className="btn btn-secondary btn-sm" onClick={() => setShowCopyModal(true)}>Copy Org Data</button>
              )}
              {selectedIntent && (
                <button className="btn btn-secondary btn-sm" onClick={() => setShowImportModal(true)}>Import CSV</button>
              )}
            </div>

            <div className="main-content-area">
              {selectedIntent ? (
                <>
                  <div className="section-header">
                    <h3>Examples for {selectedIntent.intent}</h3>
                  </div>
                  <div className="example-input-container">
                    <div className="add-input-group">
                      <input
                        type="text"
                        value={newExample}
                        ref={exampleInputRef}
                        onChange={handleExampleInput}
                        onKeyDown={handleExampleKeyDown}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddExample()}
                        placeholder="New example (type @ to mention a slot)"
                        autoComplete="off"
                      />
                      <button className="btn btn-primary" onClick={handleAddExample}>Add</button>
                    </div>
                    {showSlotDropdown && filteredSlots.length > 0 && (
                      <div className="slot-dropdown">
                        {filteredSlots.map((slot, idx) => (
                          <div
                            key={slot.id}
                            className={`slot-dropdown-item ${idx === slotDropdownIndex ? 'active' : ''}`}
                            onMouseDown={() => insertSlotMention(slot.slot)}
                            onMouseEnter={() => setSlotDropdownIndex(idx)}
                          >
                            {slot.slot}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="list-container">
                    {loadingExamples ? <p style={{ padding: '15px' }}>Loading...</p> : intentExamples.map((example) => (
                      <div key={example.id} className="list-item">
                        {editingExample?.id === example.id ? (
                          <input
                            className="form-control"
                            value={editingExample.intent_examples}
                            onChange={(e) => setEditingExample({ ...editingExample, intent_examples: e.target.value })}
                            onBlur={handleUpdateExample}
                            onKeyPress={(e) => e.key === 'Enter' && handleUpdateExample()}
                            autoFocus
                          />
                        ) : (
                          <span onClick={() => setEditingExample(example)}>{example.intent_examples}</span>
                        )}
                        <button className="delete-btn" onClick={() => handleDeleteExample(example)}>×</button>
                      </div>
                    ))}
                  </div>
                </>
              ) : selectedSlot ? (
                <>
                  <div className="section-header">
                    <h3>Questions for {selectedSlot.slot}</h3>
                  </div>
                  <div className="add-input-group">
                    <input
                      type="text"
                      value={newQuestion}
                      onChange={(e) => setNewQuestion(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddQuestion()}
                      placeholder="New question"
                    />
                    <button className="btn btn-primary" onClick={handleAddQuestion}>Add</button>
                  </div>
                  <div className="list-container">
                    {loadingQuestions ? <p style={{ padding: '15px' }}>Loading...</p> : slotQuestions.map((question) => (
                      <div key={question.id} className="list-item">
                        {editingQuestion?.id === question.id ? (
                          <input
                            className="form-control"
                            value={editingQuestion.slot_questions}
                            onChange={(e) => setEditingQuestion({ ...editingQuestion, slot_questions: e.target.value })}
                            onBlur={handleUpdateQuestion}
                            onKeyPress={(e) => e.key === 'Enter' && handleUpdateQuestion()}
                            autoFocus
                          />
                        ) : (
                          <span onClick={() => setEditingQuestion(question)}>{question.slot_questions}</span>
                        )}
                        <button className="delete-btn" onClick={() => handleDeleteQuestion(question)}>×</button>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="empty-state">
                  <p>Select an intent or slot from the sidebar to manage its data</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="rasa-manager-footer">
          <button className="btn btn-success" onClick={handleSave} disabled={training}>Save</button>
          <button
            className={`btn ${trainingStatus === 'finished' ? 'btn-success' : 'btn-primary'}`}
            onClick={handleTrain}
            disabled={training || trainingStatus === 'finished' || !isSaved}
            title={!isSaved ? "Please save changes before training" : ""}
          >
            {trainingStatus === 'training' ? 'Training...' : (trainingStatus === 'finished' ? 'Training Finished' : 'Train RASA')}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>

        {showLogs && (
          <div className="rasa-checkpoint-overlay">
            <div className="rasa-checkpoint-modal">
              <div className="checkpoint-header">
                <h3>RASA Training Progress</h3>
                <button className="close-checkpoint" onClick={() => setShowLogs(false)}>×</button>
              </div>

              <div className="checkpoint-metadata">
                <div className="meta-item">
                  <span className="label">Port:</span>
                  <span className="value">{6000 + (isNaN(parseInt(viewMode === 'organisation' ? selectedOrgId : (viewMode === 'default' ? 'admin' : userId))) ? 0 : parseInt(viewMode === 'organisation' ? selectedOrgId : (viewMode === 'default' ? 'admin' : userId)))}</span>
                </div>
                <div className="meta-item">
                  <span className="label">Mode:</span>
                  <span className="value">{viewMode === 'organisation' ? 'Organisation' : (viewMode === 'default' ? 'Default' : 'My Intents')}</span>
                </div>
                {viewMode === 'organisation' && (
                  <div className="meta-item">
                    <span className="label">Organisation:</span>
                    <span className="value">{organisations.find(o => o.id === selectedOrgId)?.organisation_name || selectedOrgId}</span>
                  </div>
                )}
              </div>

              <div className="checkpoint-progress-container">
                <div className="progress-bar-wrapper">
                  <div
                    className="progress-bar-fill"
                    style={{
                      width: currentStage === 'COMPLETE' ? '100%' :
                        currentStage === 'TRAINING' ? '75%' :
                          currentStage === 'CONFIG' ? '50%' :
                            currentStage === 'INIT' ? '25%' : '10%'
                    }}
                  ></div>
                </div>
                <div className="progress-percentage">
                  {currentStage === 'COMPLETE' ? '100%' :
                    currentStage === 'TRAINING' ? '75%' :
                      currentStage === 'CONFIG' ? '50%' :
                        currentStage === 'INIT' ? '25%' : '10%'}
                </div>
              </div>

              <div className="checkpoint-list">
                {[
                  { id: 'PARSING', label: 'Parsing Intents & Slots' },
                  { id: 'INIT', label: 'Initializing RASA Project' },
                  { id: 'CONFIG', label: 'Generating Configuration' },
                  { id: 'TRAINING', label: 'Training Model' },
                  { id: 'COMPLETE', label: 'Server Ready' }
                ].map((step, idx, arr) => {
                  const isDone = arr.findIndex(s => s.id === currentStage) >= idx || currentStage === 'COMPLETE';
                  const isActive = currentStage === step.id;
                  const isFailed = currentStage === 'FAILED' && isActive;

                  return (
                    <div key={step.id} className={`checkpoint-item ${isDone ? 'done' : ''} ${isActive ? 'active' : ''} ${isFailed ? 'failed' : ''}`}>
                      <div className="checkpoint-icon">
                        {isDone ? '✓' : (isFailed ? '✗' : (isActive ? <div className="spinner-small"></div> : ''))}
                      </div>
                      <div className="checkpoint-label">{step.label}</div>
                    </div>
                  );
                })}
              </div>

              <div className="captured-data-section">
                <div className="data-group">
                  <h4>Intents Captured</h4>
                  <div className="data-tags">
                    {capturedIntents.length > 0 ? capturedIntents.map(intent => (
                      <span key={intent} className="data-tag intent">{intent}</span>
                    )) : <span className="no-data">Waiting for parsing...</span>}
                  </div>
                </div>
                <div className="data-group">
                  <h4>Entities Captured</h4>
                  <div className="data-tags">
                    {capturedEntities.length > 0 ? capturedEntities.map(entity => (
                      <span key={entity} className="data-tag entity">{entity}</span>
                    )) : <span className="no-data">None detected yet</span>}
                  </div>
                </div>
              </div>

              {currentStage === 'COMPLETE' && (
                <div className="success-message-area">
                  <div className="success-banner">
                    <span className="success-icon">🎉</span>
                    <div className="success-text">
                      <strong>Training Successful!</strong>
                      <p>The RASA server is now running and ready to handle requests.</p>
                    </div>
                  </div>
                </div>
              )}

              {currentStage === 'FAILED' && (
                <div className="success-message-area">
                  <div className="success-banner failed" style={{ background: '#ffebee', borderColor: '#ffcdd2' }}>
                    <span className="success-icon">⚠️</span>
                    <div className="success-text">
                      <strong style={{ color: '#c62828' }}>Training Failed</strong>
                      <p style={{ color: '#e53935' }}>An error occurred during training. Please check the logs.</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="checkpoint-footer">
                <button className="btn btn-secondary" onClick={() => setShowLogs(false)}>
                  {currentStage === 'COMPLETE' ? 'Done' : 'Close'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="rasa-manager-overlay" style={{ zIndex: 2000 }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: '10px', padding: '2rem', minWidth: '400px', boxShadow: '0 4px 24px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Import Examples for {selectedIntent?.intent}</h3>
              <button onClick={() => setShowImportModal(false)} className="delete-btn" style={{ fontSize: '1.5rem' }}>×</button>
            </div>
            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1.5rem' }}>CSV must have an "examples" column header</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <input type="file" accept=".csv" onChange={(e) => setCsvFile(e.target.files[0])} style={{ padding: '10px', border: '1px dashed #ced4da', borderRadius: '6px' }} />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button className="btn btn-secondary" onClick={() => setShowImportModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleCSVUpload} disabled={csvUploading}>
                  {csvUploading ? 'Uploading...' : 'Upload CSV'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Copy Modal */}
      {showCopyModal && (
        <div className="rasa-manager-overlay" style={{ zIndex: 2000 }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: '10px', padding: '2rem', minWidth: '400px', boxShadow: '0 4px 24px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Copy Data to {organisations.find(o => o.id == selectedOrgId)?.name}</h3>
              <button onClick={() => setShowCopyModal(false)} className="delete-btn" style={{ fontSize: '1.5rem' }}>×</button>
            </div>
            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1.5rem' }}>Select source organisation to copy intents and slots from.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <select
                className="org-select"
                value={sourceOrgIdForCopy}
                onChange={(e) => setSourceOrgIdForCopy(e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="">Select Source Organisation</option>
                {organisations.filter(o => o.id != selectedOrgId).map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button className="btn btn-secondary" onClick={() => setShowCopyModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleCopyOrganisation} disabled={!sourceOrgIdForCopy}>
                  Copy Data
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RasaIntentManager;

